import { Pill, Rating, Text, Title } from "@clipboard-health/ui-components";
import { isDefined } from "@clipboard-health/util-ts";
import { Box, DialogContent, Fade, Stack } from "@mui/material";
import { APP_V2_USER_EVENTS, logEvent } from "@src/appV2/lib/analytics";
import { DialogFooter } from "@src/appV2/redesign/components/DialogFooter";
import { LoadingButton } from "@src/appV2/redesign/components/LoadingButton";
import { TextButton } from "@src/appV2/redesign/components/TextButton";
import { WorkplaceAvatar } from "@src/appV2/redesign/components/WorkplaceAvatar";
import { getGradientByTimeSlot } from "@src/appV2/redesign/Shift/getGradientByTimeSlot";
import { getTimeSlotLabel } from "@src/appV2/redesign/Shift/getTimeSlotLabel";
import { iconTypeByTimeSlotFilled } from "@src/appV2/redesign/Shift/TimeSlotIcon";
import { shiftPeriodToTimeSlot, type WorkerShift } from "@src/appV2/Shifts/Shift/types";
// eslint-disable-next-line no-restricted-imports -- legacy, use @clipboard-health/date-time. See https://www.notion.so/BP-Date-Time-2ef8643321f48032ad80e8a5b30aa278 for more information
import { format, parseISO } from "date-fns";
import { useState } from "react";

import { useIsWorkplaceReviewUXUpdateEnabled } from "../hooks/useIsWorkplaceReviewUXUpdateEnabled";
import { type StepAction } from "../types";

export interface RateWorkplaceStepProps {
  facilityName: string;
  facilityPhoto?: string;
  isSubmitting?: boolean;
  onAction: (action: StepAction) => void;
  isDelayedReview?: boolean;
  shift?: Pick<WorkerShift, "name" | "start">;
}

function formatShiftDatePillLabel(shiftStartTime: string, timeSlotLabel: string): string {
  const date = parseISO(shiftStartTime);
  const formattedDate = format(date, "EEE, MMM d");
  return `${timeSlotLabel} | ${formattedDate}`;
}

export function RateWorkplaceStep(props: RateWorkplaceStepProps) {
  const {
    facilityName,
    facilityPhoto,
    isSubmitting = false,
    onAction,
    isDelayedReview = false,
    shift,
  } = props;
  const { isEnabled: isWriteReviewUiUpdateEnabled } = useIsWorkplaceReviewUXUpdateEnabled();
  const [selectedRating, setSelectedRating] = useState(0);

  const timeSlot = isDefined(shift) ? shiftPeriodToTimeSlot[shift.name] : undefined;
  const shiftStartTime = shift?.start;
  const showShiftPill = isDelayedReview && isDefined(timeSlot) && isDefined(shiftStartTime);

  const commonButtonProps = {
    fullWidth: true,
    variant: "contained" as const,
    size: "large" as const,
    isLoading: isSubmitting,
    onClick: () => {
      onAction({
        type: "SUBMIT_RATING",
        rating: selectedRating,
      });
    },
  };

  return (
    <>
      <DialogContent sx={{ display: "flex", alignItems: "center" }}>
        <Stack spacing={6} alignItems="center" sx={{ px: 0 }}>
          <WorkplaceAvatar pictureUrl={facilityPhoto} size="large" />

          {showShiftPill && (
            <Pill
              size="small"
              variant="filled"
              label={formatShiftDatePillLabel(shiftStartTime, getTimeSlotLabel(timeSlot))}
              iconType={iconTypeByTimeSlotFilled[timeSlot]}
              color={(theme) => theme.palette.text.primary}
              iconColor={(theme) => theme.palette.text.primary}
              sx={{
                width: "fit-content",
                background: (theme) => getGradientByTimeSlot({ timeSlot, theme }),
              }}
            />
          )}

          <Title variant="h3" component="h3" textAlign="center">
            {isWriteReviewUiUpdateEnabled
              ? `Rate ${facilityName}`
              : `Rate your shift at ${facilityName}`}
          </Title>

          {isWriteReviewUiUpdateEnabled ? (
            <Text variant="body1" textAlign="center" color="text.secondary">
              Professionals rely on workplace ratings to decide where to work.
            </Text>
          ) : (
            !isDelayedReview && (
              <Text variant="body1" textAlign="center" color="text.secondary">
                Select a rating for your shift experience
              </Text>
            )
          )}

          <Box sx={{ pt: 1 }}>
            <Rating
              precision={1}
              value={selectedRating}
              size="large"
              disabled={isSubmitting}
              onChange={(_, value) => {
                // Prevent unselecting a star once selected
                if (value !== null && value > 0) {
                  setSelectedRating(value);
                }
              }}
            />
          </Box>
        </Stack>
      </DialogContent>

      <DialogFooter orientation="vertical">
        {/* hardcoded height to avoid layout shift "I'll do this later" button gets hidden (DialogContent has vertical centering and stars "jump" elsewise) */}
        <Stack spacing={3} sx={{ width: "100%", height: "100px" }}>
          {isDelayedReview ? (
            <>
              {selectedRating > 0 && (
                <Fade in appear>
                  <Box>
                    <LoadingButton {...commonButtonProps}>Continue</LoadingButton>
                  </Box>
                </Fade>
              )}
              {selectedRating === 0 && (
                <Fade in appear>
                  <Stack spacing={3} alignItems="center">
                    {!isWriteReviewUiUpdateEnabled && (
                      <Text variant="body2" color="text.secondary">
                        This review will take less than 2 minutes
                      </Text>
                    )}
                    <TextButton
                      sx={{ alignSelf: "center" }}
                      onClick={() => {
                        logEvent(APP_V2_USER_EVENTS.DELAYED_WORKPLACE_REVIEW_SKIPPED, {
                          facilityName,
                          shift: shift?.name,
                          shiftStartTime: shift?.start,
                        });
                        onAction({ type: "CLOSE_FLOW" });
                      }}
                    >
                      Skip Review
                    </TextButton>
                  </Stack>
                </Fade>
              )}
            </>
          ) : (
            // Default behavior: show disabled/enabled Continue button + "I'll do it later" option
            <>
              <LoadingButton {...commonButtonProps} disabled={selectedRating === 0}>
                Continue
              </LoadingButton>
              {selectedRating === 0 && !isSubmitting && !isWriteReviewUiUpdateEnabled && (
                <TextButton
                  sx={{ alignSelf: "center" }}
                  onClick={() => {
                    onAction({ type: "CLOSE_FLOW" });
                  }}
                >
                  I&apos;ll do this later
                </TextButton>
              )}
            </>
          )}
        </Stack>
      </DialogFooter>
    </>
  );
}
